import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import ActionButtons from "../../components/common/ActionButtons";
import {
  useGetAccessLevelsQuery,
  useCreateAccessLevelMutation,
  useUpdateAccessLevelMutation,
  useDeleteAccessLevelMutation,
} from "../../store/api";

const AccessLevels = () => {
  const { data, isLoading, isError, error } = useGetAccessLevelsQuery();
  const [createAccessLevel, createState] = useCreateAccessLevelMutation();
  const [updateAccessLevel, updateState] = useUpdateAccessLevelMutation();
  const [deleteAccessLevel] = useDeleteAccessLevelMutation();

  const [form, setForm] = useState({
    name: "",
    description: "",
    sortOrder: 0,
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    accessLevelId: null,
    accessLevelName: "",
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const accessLevels = Array.isArray(data) ? data : [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        description: form.description,
        sortOrder: Number(form.sortOrder),
        isActive: form.isActive,
      };
      if (editingId) {
        await updateAccessLevel({
          accessLevelId: editingId,
          payload,
        }).unwrap();
        setEditingId(null);
      } else {
        await createAccessLevel(payload).unwrap();
      }
      setForm({ name: "", description: "", sortOrder: 0, isActive: true });
      setShowForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (accessLevel) => {
    setEditingId(accessLevel.accessLevelId);
    setForm({
      name: accessLevel.name || "",
      description: accessLevel.description || "",
      sortOrder: accessLevel.sortOrder ?? 0,
      isActive:
        accessLevel.isActive !== undefined ? accessLevel.isActive : true,
    });
    setShowForm(true);
  };

  const handleDelete = (accessLevelId) => {
    const accessLevel = accessLevels.find(
      (a) => a.accessLevelId === accessLevelId,
    );
    setDeleteModal({
      isOpen: true,
      accessLevelId,
      accessLevelName: accessLevel?.name || "this access level",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteAccessLevel(deleteModal.accessLevelId).unwrap();
      setDeleteModal({
        isOpen: false,
        accessLevelId: null,
        accessLevelName: "",
      });
    } catch (err) {
      setDeleteModal({
        isOpen: false,
        accessLevelId: null,
        accessLevelName: "",
      });
      const errorMessage =
        err?.data?.detail ||
        err?.data?.message ||
        "Failed to delete access level. It may be assigned to users.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({ name: "", description: "", sortOrder: 0, isActive: true });
    setShowForm(false);
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
    { key: "sortOrder", label: "Sort Order" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = accessLevels.map((accessLevel) => {
    return {
      id: accessLevel.accessLevelId,
      name: accessLevel.name,
      description: accessLevel.description || "—",
      sortOrder: accessLevel.sortOrder ?? 0,
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {accessLevel.isActive ? "Active" : "Inactive"}
        </span>
      ),
      actions: (
        <ActionButtons
          actions={[
            {
              icon: "edit",
              label: "Edit",
              color: "primary",
              onClick: () => handleEdit(accessLevel),
            },
            {
              icon: "delete",
              label: "Delete",
              color: "danger",
              onClick: () => handleDelete(accessLevel.accessLevelId),
            },
          ]}
        />
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Access Levels"
        subtitle="Manage user access levels and permissions."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Add Access Level
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Access Level" : "Add Access Level"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <Input
              label="Name"
              name="name"
              placeholder="POS_MANAGER"
              value={form.name}
              onChange={handleChange}
              required
              helper="Unique identifier (e.g., POS_ADMIN, POS_STAFF)"
            />

            <Input
              label="Description"
              name="description"
              placeholder="Can manage daily operations and staff"
              value={form.description}
              onChange={handleChange}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Sort Order"
                name="sortOrder"
                type="number"
                placeholder="0"
                value={form.sortOrder}
                onChange={handleChange}
                helper="Lower numbers appear first"
              />
              <Checkbox
                label="Active"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
              />
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Access level {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save access level."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Access Level
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 dark:border-slate-700 px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
            All Access Levels
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading access levels...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load access levels."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table
                columns={columns}
                rows={rows}
                emptyText="No access levels yet."
              />
            )}
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            accessLevelId: null,
            accessLevelName: "",
          })
        }
        title="Delete Access Level"
        confirmText="Delete"
        confirmVariant="primary"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.accessLevelName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This action cannot be undone. The access level cannot be deleted if
          it's assigned to users.
        </p>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default AccessLevels;
